import type { CenterProps, MotionPropsFor } from '@hover/blueprint';
import { Badge, forwardRef, motion } from '@hover/blueprint';
import { AnimatePresence } from 'framer-motion';
import React from 'react';

import { useGeneratedImageLabel } from 'src/modules/homeownerApp/hooks';
import type { AiGeneratedImageFragment } from 'src/shared/graphql/generated';

const MotionBadge = motion(Badge);

interface EditedBadgeProps extends MotionPropsFor<CenterProps> {
  /**
   * The image to display the badge for. This is used to determine whether the
   * badge is displayed.
   */
  image: AiGeneratedImageFragment | undefined | null;
  isLayoutAnimated?: boolean;
  /**
   * Additional flag to control the visiblity of the badge (in addition to the
   * edited state of the provided image).
   *
   * @defaultValue true
   */
  isVisible?: boolean;
}

/**
 * Badge that indicates whether an image has been edited.
 *
 * @todo(design-system) use the `Badge` component here once we've themed it for
 * the `/next` theme.
 * @todo(homeowner-components) consolidate this with the edited badge in projects
 */
const EditedBadge = forwardRef<EditedBadgeProps, 'div'>(
  ({ image, isLayoutAnimated, isVisible = true, ...props }, ref) => {
    const badgeLabel = useGeneratedImageLabel(image);

    if (!image) return null;

    return (
      <AnimatePresence initial={false}>
        {isVisible && (
          <MotionBadge
            animate={{ opacity: 1 }}
            colorMode="dark"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            layout
            left={{ base: 400, tablet: 600 }}
            position="absolute"
            top={{ base: 400, tablet: 600 }}
            transform={isLayoutAnimated ? undefined : 'none !important'}
            variant="glass"
            zIndex={2}
            {...props}
            ref={ref}
          >
            {badgeLabel}
          </MotionBadge>
        )}
      </AnimatePresence>
    );
  },
);

export { EditedBadge };
